<!--
  landing-page.vue

  2026-04-14: Fixed hero_layout, show_search, search_placeholder, search_variant
              to persist via custom_settings JSONB instead of non-existent top-level columns
-->
<template>
  <div>
    <v-card flat>
      <v-card-title class="text-h5 font-weight-bold">
        {{ $t('Landing Page') }}
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 mb-4">
              <v-card-title class="text-subtitle-1 px-0 pt-0">
                {{ $t('Header Content') }}
              </v-card-title>

              <v-text-field
                v-model="formData.sections.header.title"
                dense
                outlined
                :rules="[required]"
                :label="$t('Title')"
                @input="updateForm"
              />

              <v-text-field
                v-model="formData.sections.header.sub_title"
                dense
                outlined
                :label="$t('Subtitle')"
                @input="updateForm"
              />
            </v-card>

            <v-card outlined class="pa-4 mb-4">
              <v-card-title class="text-subtitle-1 px-0 pt-0">
                {{ $t('Layout & Search') }}
              </v-card-title>

              <v-select
                v-model="formData.hero_layout"
                :items="heroLayoutOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                :label="$t('Hero Layout')"
                @change="updateForm"
              />

              <v-switch
                v-if="formData.hero_layout === 'hero_pill'"
                v-model="formData.show_search"
                :label="$t('Show Integrated Search')"
                dense
                class="mt-0"
                @change="updateForm"
              />

              <v-text-field
                v-if="formData.hero_layout === 'hero_pill'"
                v-model="formData.search_placeholder"
                dense
                outlined
                :label="$t('Search Placeholder')"
                placeholder="Where are you going?"
                @input="updateForm"
              />

              <v-select
                v-model="formData.search_variant"
                :items="searchVariantOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                :label="$t('Search Bar Style')"
                @change="updateForm"
              />
            </v-card>

            <v-card outlined class="pa-4 mb-4">
              <v-card-title class="text-subtitle-1 px-0 pt-0">
                {{ $t('Images') }}
              </v-card-title>

              <v-row>
                <v-col cols="12">
                  <v-input :value="formData.bg_image" :rules="[required]" />
                  <gallery
                    :key="formData.bg_image"
                    button-text="Background image"
                    folder="booking-engine"
                    :compress="false"
                    :cols="4"
                    :thumbnails="true"
                    readonly
                    :images="formData.bg_image ? [formData.bg_image] : []"
                    :on-change="afterBgUpload"
                    :uploadable="true"
                  />
                </v-col>

                <v-col cols="12">
                  <gallery
                    :key="formData.logo"
                    button-text="Site Logo *"
                    folder="booking-engine"
                    :compress="false"
                    :cols="4"
                    readonly
                    :thumbnails="true"
                    :images="formData.logo ? [formData.logo] : []"
                    :on-change="afterLogoUpload"
                    :uploadable="true"
                  />
                </v-col>

                <v-col cols="12">
                  <gallery
                    :key="formData.favicon"
                    button-text="Favicon *"
                    folder="booking-engine"
                    :compress="false"
                    :cols="4"
                    readonly
                    :thumbnails="true"
                    :images="formData.favicon ? [formData.favicon] : []"
                    :on-change="afterFaviconUpload"
                    :uploadable="true"
                  />
                </v-col>
              </v-row>
            </v-card>

            <v-card outlined class="pa-4">
              <v-card-title class="text-subtitle-1 px-0 pt-0">
                {{ $t('Destinations') }}
              </v-card-title>

              <v-select
                v-model="formData.destination_cities"
                :label="$t('Cities')"
                outlined
                item-text="name"
                return-object
                dense
                multiple
                :hide-details="true"
                :items="destinationCities"
                @change="updateForm"
              />

              <v-row class="mt-4">
                <v-col
                  v-for="(city, key) in formData.destination_cities"
                  :key="key"
                  cols="12"
                >
                  <gallery
                    :button-text="city.name"
                    folder="booking-engine"
                    :compress="false"
                    :cols="4"
                    :thumbnails="true"
                    :images="city.image ? [city.image] : []"
                    :on-change="imgs => afterCityUpload(imgs, city.name, key)"
                    :uploadable="true"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <preview-frame
              :url="previewUrl"
              :needs-refresh="needsRefresh"
              @refresh="refreshPreview"
              @load="onFrameLoad"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FormRulesMixin from 'components/mixins/form-rules-mixin'
import Gallery from 'components/common/gallery.vue'
import PreviewFrame from '../components/preview-frame.vue'
import axios from 'axios'

export default {
  name: 'LandingPage',

  components: {
    Gallery,
    PreviewFrame,
  },

  mixins: [FormRulesMixin],

  props: {
    bookingEngineSetting: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      needsRefresh: false,
      isFrameLoading: true,
      formData: {
        name: this.bookingEngineSetting.name,
        sections: {
          header: {
            title: this.bookingEngineSetting.sections?.header?.title || '',
            sub_title:
              this.bookingEngineSetting.sections?.header?.sub_title || '',
          },
        },
        bg_image: this.bookingEngineSetting.bg_image || null,
        logo: this.bookingEngineSetting.logo || null,
        favicon: this.bookingEngineSetting.favicon || null,
        destination_cities: this.bookingEngineSetting.destination_cities || [],
        hero_layout:
          this.bookingEngineSetting.custom_settings?.hero_layout || 'centered',
        show_search:
          this.bookingEngineSetting.custom_settings?.show_search !== false,
        search_placeholder:
          this.bookingEngineSetting.custom_settings?.search_placeholder || '',
        search_variant:
          this.bookingEngineSetting.custom_settings?.search_variant || 'inline',
      },
    }
  },

  computed: {
    ...mapGetters(['listingCities']),

    destinationCities() {
      return this.listingCities.map(city => ({ name: city, image: null }))
    },

    previewUrl() {
      // For development, use localhost. In production, use bookingsboom.com domain
      return process.env.NODE_ENV === 'development'
        ? 'http://localhost:8081/'
        : `https://${this.bookingEngineSetting.name}.bookingsboom.com`
    },

    showError() {
      return !this.$refs.form?.validate()
    },

    heroLayoutOptions() {
      return [
        { text: 'Centered', value: 'centered' },
        { text: 'Left Aligned', value: 'left_aligned' },
        { text: 'Split', value: 'split' },
        { text: 'Hero + Pill Search', value: 'hero_pill' },
      ]
    },

    searchVariantOptions() {
      return [
        { text: 'Inline', value: 'inline' },
        { text: 'Stacked', value: 'stacked' },
        { text: 'Floating', value: 'floating' },
        { text: 'Pill', value: 'pill' },
      ]
    },
  },

  methods: {
    updateForm() {
      const updateData = {
        sections: { ...this.formData.sections },
        bg_image: this.formData.bg_image,
        logo: this.formData.logo,
        favicon: this.formData.favicon,
        destination_cities: this.formData.destination_cities,
        custom_settings: {
          ...this.bookingEngineSetting.custom_settings,
          hero_layout: this.formData.hero_layout,
          show_search: this.formData.show_search,
          search_placeholder: this.formData.search_placeholder,
          search_variant: this.formData.search_variant,
        },
      }

      this.$emit('update:setting', updateData)
      this.needsRefresh = true
    },

    afterBgUpload(imgs) {
      this.formData.bg_image = imgs[0]
      this.updateForm()
    },

    afterLogoUpload(imgs) {
      this.formData.logo = imgs[0]
      this.updateForm()
    },

    afterFaviconUpload(imgs) {
      this.formData.favicon = imgs[0]
      this.updateForm()
    },

    afterCityUpload(imgs, name, key) {
      this.formData.destination_cities[key] = {
        name,
        image: imgs[0],
      }
      this.$forceUpdate()
      this.updateForm()
    },

    validate() {
      return this.$refs.form.validate()
    },

    async refreshPreview() {
      if (!this.$refs.form?.validate()) return

      try {
        this.isFrameLoading = true

        // Save changes to backend first
        await axios.patch(
          `/api/booking-engine-settings/${this.bookingEngineSetting.id}`,
          {
            name: this.bookingEngineSetting.name,
            sections: this.formData.sections,
            bg_image: this.formData.bg_image,
            logo: this.formData.logo,
            favicon: this.formData.favicon,
            destination_cities: this.formData.destination_cities,
            custom_settings: {
              ...this.bookingEngineSetting.custom_settings,
              hero_layout: this.formData.hero_layout,
              show_search: this.formData.show_search,
              search_placeholder: this.formData.search_placeholder,
              search_variant: this.formData.search_variant,
            },
          }
        )

        this.needsRefresh = false
      } catch (error) {
        console.error('Failed to save settings:', error)
        this.$store.dispatch('snackbar/showSnackbar', {
          message: this.$t('Failed to save settings'),
          color: 'error',
        })
      }
    },

    onFrameLoad() {
      this.isFrameLoading = false
    },
  },
}
</script>

<style scoped>
.v-text-field ::v-deep .v-input__append-inner {
  margin-top: 4px !important;
}

.preview-container {
  position: relative;
}

.preview-frame-container {
  position: relative;
}

iframe {
  border: none;
}
</style>
